/* eslint-disable react/no-array-index-key */
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Title } from 'ui-library';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ButtonsBlockRow from 'components/ButtonsBlockRow';
import { InfoErrorList } from 'components/InfoErrorList/InfoErrorList';

import './PortfolioProcessLayout.css';
import { CreatePortfolioManager } from 'datasource/CreatePortfolio';
import { PortfolioProcessLayoutManager } from 'datasource/PortfolioProcessLayout';
import { PRODUCT_EXTERNAL_ID, QUALITY_CHECK_DATA } from 'constants/constants';
import StepperLayout from '../Stepper/Stepper';
import { PortfolioFlowLayoutContract } from './PortfolioProcessLayout.types';

interface PortfolioFlowLayoutProps {
    dataSource: () => PortfolioFlowLayoutContract;
    children: React.ReactNode;
}

export const PortfolioProcessLayout = ({
    dataSource,
    children,
}: PortfolioFlowLayoutProps) => {
    const {
        stepper,
        footerAdditionalInfo,
        onBack,
        onContinue,
        continueLabel,
        continueDisabled,
        backLabel,
        backDisabled,
        pageErrors,
        onAdditionalComponentClick,
        additionalComponentLabel,
        inProgress,
        isStepperEnabled,
        isFooterSimplified,
        contentTitle,
        contentClassName,
        hideStepperButtons,
        productType,
        currentStepSelector,
    } = dataSource();

    const location = useLocation();
    const navigate = useNavigate();
    const { dfsClientId } = useParams();

    const {
        t,
        i18n: { language },
    } = useTranslation();

    const contentClassnames = useMemo(() => {
        if (!contentClassName || contentClassName === 'portfolio-process-content-wrapper') {
            return 'portfolio-process-content-wrapper';
        }

        return `portfolio-process-content-wrapper ${contentClassName}`;
    }, [contentClassName]);

    const ButtonsBlock = useMemo(() => (
        <ButtonsBlockRow
            additionalButton={{
                text: backLabel || t('portfolio.back'),
                onClick: onBack,
                disabled: backDisabled,
            }}
            additionalComponent={onAdditionalComponentClick
                && (
                    <Button
                        type="secondary"
                        disabled={continueDisabled}
                        onClick={onAdditionalComponentClick}
                    >
                        {additionalComponentLabel}
                    </Button>
                )}
            primaryButton={{
                text: continueLabel || t('portfolio.continue'),
                onClick: onContinue,
                disabled: continueDisabled,
                loading: inProgress,
            }}
        />
    ), [
        language,
        backLabel,
        backDisabled,
        onAdditionalComponentClick,
        continueDisabled,
        additionalComponentLabel,
        continueLabel,
        onBack,
        onContinue,
        continueDisabled,
        inProgress,
    ]);

    useEffect(() => {
        window.history.pushState(null, '', window.location.href);
        const handleBackButton = () => {
            window.history.go(1);
        };

        window.addEventListener('popstate', handleBackButton);

        const timeout = setTimeout(() => {
            if (location?.pathname && !location?.state && !!currentStepSelector && !location?.pathname?.includes(currentStepSelector?.url)) {
                sessionStorage.removeItem('Steppers');
                sessionStorage.removeItem(CreatePortfolioManager.LOCAL_STORAGE_KEY);
                sessionStorage.removeItem(PortfolioProcessLayoutManager.LOCAL_STORAGE_KEY);
                sessionStorage.removeItem(QUALITY_CHECK_DATA);
                sessionStorage.removeItem(PRODUCT_EXTERNAL_ID);
                navigate(`/client/${dfsClientId}/dashboard`);
            }
        }, 2000);

        return () => {
            window.removeEventListener('popstate', handleBackButton);
            clearTimeout(timeout);
        };
    }, [currentStepSelector, location?.pathname, location?.state, navigate, dfsClientId]);

    return (
        <div className="PortfolioProcessLayout">
            <div className="fi-body">
                {isStepperEnabled && (
                    <StepperLayout
                        config={stepper.steps}
                        currentStepNumber={stepper.currentStepNumber}
                        title={stepper.title}
                        productType={productType}
                    />
                )}
                <div className="container">
                    {stepper?.steps?.length > 0 ? (
                        <>
                            <div className={contentClassnames}>
                                {contentTitle && (
                                    <Title type={1} className="font-normal">
                                        {t(contentTitle)}
                                    </Title>
                                )}

                                {children}
                            </div>
                            <InfoErrorList errors={pageErrors}/>
                        </>
                    ) : ''}

                </div>
            </div>

            {isFooterSimplified ? (
                <div className="fi-footer">
                    <div className="container">
                        <div className="fi-footer-data">
                            <div className="fi-footer-client-details">
                                {
                                    footerAdditionalInfo.map(({
                                        label,
                                        values,
                                    }, i) => (
                                        <div
                                            key={`item${i}`}
                                            className="fi-footer-client-details__block fi-footer-client-details__block--age"
                                        >
                                            <div className="fi-footer-client-details_label">{t(label)}</div>
                                            {
                                                values.map((value, j) => (
                                                    <div
                                                        key={`value${j}`}
                                                        className="fi-footer-client-details__value"
                                                    >
                                                        {value}
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    ))
                                }
                            </div>
                            {!hideStepperButtons && ButtonsBlock}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="container">
                    {!hideStepperButtons && ButtonsBlock}
                </div>
            )}
        </div>
    );
};
